import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronRight,
  Diagram2,
  FileEarmark,
  FolderFill,
  Folder2Open,
  FileEarmarkPlus,
  FolderPlus,
  PencilSquare,
  Trash,
  Upload,
  VectorPen,
} from "react-bootstrap-icons";
import {
  diagramKindOf,
  withExtension,
  UPLOAD_ACCEPT,
  DIAGRAM_EXTENSIONS,
  type DiagramKind,
} from "../lib/diagramKind";
import { freeFormTemplateScene } from "../lib/freeformTemplates";
import { useDialogs } from "./ui/dialogs";
import { Button } from "./ui/Button";
import { cn } from "../lib/cn";

export type CatalogNode = {
  name: string;
  type: "folder" | "file";
  children?: CatalogNode[];
  canEdit?: boolean;
  /**
   * Authority over the shape of the subtree -- creating, renaming and deleting
   * folders. Separate from canEdit: an editor changes a diagram's contents, a
   * manager changes what folders exist. Absent from an older server, which is
   * why the fallback below treats it as off rather than inheriting canEdit.
   */
  canManage?: boolean;
};

export type CatalogTreeProps = {
  catalog: CatalogNode[];
  onSelectFile: (filePath: string) => void;
  selectedFile?: string;
  onCatalogUpdate: () => void;
  isAdmin?: boolean;
  role: string;
  apiUrl?: string;
  token?: string;
  openPaths: string[];
  onToggleFolder: (path: string, isOpen: boolean) => void;
  onRenamePath?: (oldPath: string, newPath: string, isFolder: boolean) => void;
  /** Highlights matching substrings when the catalog is filtered. */
  searchQuery?: string;
};

const MINIMAL_DIAGRAM_XML = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
                  id="Definitions_1">
  <bpmn:process id="Process_1" isExecutable="false">
    <bpmn:startEvent id="StartEvent_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="StartEvent_1_di" bpmnElement="StartEvent_1">
        <dc:Bounds x="250" y="150" width="36" height="36"/>
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

const INDENT_PX = 14;

function joinPath(parentPath: string, name: string) {
  return parentPath ? `${parentPath}/${name}` : name;
}

const INVALID_NAME_CHARS = /[/\\:*?"<>|]/;

function validateName(value: string): string | null {
  if (INVALID_NAME_CHARS.test(value)) {
    return 'A name cannot contain / \\ : * ? " < > or |';
  }
  return null;
}

/** A BPMN process, a free-form sketch, or something the editor cannot open. */
function FileKindIcon({
  kind,
  className,
}: {
  kind: DiagramKind | null;
  className?: string;
}) {
  if (kind === "freeform") {
    return <VectorPen size={14} aria-label="Free-form diagram" className={className} />;
  }
  if (kind === "bpmn") {
    return <Diagram2 size={14} aria-label="BPMN diagram" className={className} />;
  }
  return <FileEarmark size={14} aria-hidden="true" className={className} />;
}

/** Renders the portion of a name matching the active search as highlighted. */
function Highlight({ text, query }: { text: string; query?: string }) {
  if (!query) return <>{text}</>;

  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return <>{text}</>;

  return (
    <>
      {text.slice(0, index)}
      <mark className="bg-accent-soft text-accent rounded-[3px] px-0.5 font-semibold">
        {text.slice(index, index + query.length)}
      </mark>
      {text.slice(index + query.length)}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Keyboard navigation                                                        */
/* -------------------------------------------------------------------------- */

type FlatRow = {
  path: string;
  parentPath: string | null;
  isFolder: boolean;
  isOpen: boolean;
  hasChildren: boolean;
};

/**
 * Flattens the currently *visible* rows in render order. This is what turns the
 * tree into something arrow keys can walk, and it deliberately skips the
 * contents of collapsed folders so ArrowDown never lands on hidden content.
 */
function flattenVisible(
  nodes: CatalogNode[],
  openPaths: string[],
  parentPath = "",
): FlatRow[] {
  const rows: FlatRow[] = [];

  for (const node of nodes) {
    const path = joinPath(parentPath, node.name);
    const isFolder = node.type === "folder";
    const isOpen = isFolder && openPaths.includes(path);
    const children = node.children ?? [];

    rows.push({
      path,
      parentPath: parentPath || null,
      isFolder,
      isOpen,
      hasChildren: children.length > 0,
    });

    if (isFolder && isOpen) {
      rows.push(...flattenVisible(children, openPaths, path));
    }
  }

  return rows;
}

/* -------------------------------------------------------------------------- */
/* Row                                                                        */
/* -------------------------------------------------------------------------- */

type RowProps = {
  node: CatalogNode;
  parentPath: string;
  depth: number;
  ctx: TreeContext;
};

/** The entry currently being dragged, or null when no drag is in progress. */
type DragSource = {
  path: string;
  isFolder: boolean;
  /** Parent folder path; "" for a root-level entry. */
  parentPath: string;
};

type TreeContext = {
  props: CatalogTreeProps;
  actions: ReturnType<typeof useCatalogActions>;
  focusedPath: string | null;
  setFocusedPath: (path: string) => void;
  registerRow: (path: string, el: HTMLDivElement | null) => void;
  drag: DragSource | null;
  setDrag: (drag: DragSource | null) => void;
  /** Folder path currently under the pointer; "" is the catalog root. */
  dropTarget: string | null;
  setDropTarget: (path: string | null) => void;
  /** Opens a closed folder after the pointer hovers it mid-drag. */
  scheduleSpringOpen: (path: string) => void;
  cancelSpringOpen: () => void;
  /**
   * Folder that files dragged in from the desktop would land in, or null when
   * no file drag is in progress. "" is the catalog root.
   */
  fileDropTarget: string | null;
  setFileDropTarget: (path: string | null) => void;
};

/**
 * True when the drag carries files from outside the browser.
 *
 * This is what separates an OS file drop from an internal move: during a drag
 * the file contents are unreadable for security reasons, but `types` is not.
 */
function isFileDrag(event: React.DragEvent): boolean {
  return Array.from(event.dataTransfer.types).includes("Files");
}

/**
 * Whether `drag` may be dropped into `folderPath` ("" = catalog root).
 *
 * Rejects the no-op cases (dropping onto itself, or back into the folder it
 * already lives in) and the destructive one: moving a folder inside its own
 * subtree, which the server does not guard against.
 */
function canDropInto(drag: DragSource | null, folderPath: string): boolean {
  if (!drag) return false;
  if (drag.path === folderPath) return false;
  if (drag.parentPath === folderPath) return false;
  if (drag.isFolder && folderPath.startsWith(`${drag.path}/`)) return false;
  return true;
}

function CatalogNodeItem({ node, parentPath, depth, ctx }: RowProps) {
  const {
    props,
    actions,
    focusedPath,
    setFocusedPath,
    registerRow,
    drag,
    setDrag,
    dropTarget,
    setDropTarget,
    scheduleSpringOpen,
    cancelSpringOpen,
    fileDropTarget,
    setFileDropTarget,
  } = ctx;
  const { selectedFile, openPaths, onToggleFolder, onSelectFile, isAdmin, searchQuery } =
    props;

  const fullPath = joinPath(parentPath, node.name);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isFolder = node.type === "folder";
  const isOpen = isFolder && openPaths.includes(fullPath);
  const isSelected = !isFolder && fullPath === selectedFile;
  const isFocused = focusedPath === fullPath;
  // Diagram-level authority: create, edit, rename and delete diagrams, and
  // drag them between folders.
  const canEditHere = Boolean(isAdmin || node.canEdit);
  // Folder-level authority: create, rename and delete folders. A plain editor
  // no longer has this -- the server refuses those calls, so offering the
  // buttons would only produce 403s.
  const canManage = Boolean(isAdmin || node.canManage);

  const isDragging = drag?.path === fullPath;
  const acceptsDrop = isFolder && canEditHere && canDropInto(drag, fullPath);
  const isDropTarget = acceptsDrop && dropTarget === fullPath;

  // Files dropped on a folder go into it; files dropped on a diagram go into the
  // folder that diagram lives in, so the whole tree is a usable drop surface.
  const uploadTarget = isFolder ? fullPath : parentPath;
  const isUploadTarget = canEditHere && fileDropTarget !== null && fileDropTarget === uploadTarget;

  const activate = () => {
    setFocusedPath(fullPath);
    if (isFolder) {
      onToggleFolder(fullPath, !isOpen);
    } else {
      onSelectFile(fullPath);
    }
  };

  // Indentation is applied as padding on the row itself rather than on a
  // wrapper, so the hover/selection background still spans the full panel width.
  const indentStyle = { paddingInlineStart: `${8 + depth * INDENT_PX}px` };

  const row = (
    <div
      ref={(el) => registerRow(fullPath, el)}
      role="treeitem"
      aria-expanded={isFolder ? isOpen : undefined}
      aria-selected={isSelected}
      aria-level={depth + 1}
      tabIndex={isFocused ? 0 : -1}
      data-path={fullPath}
      onClick={activate}
      onFocus={() => setFocusedPath(fullPath)}
      // Only entries the user may modify can be dragged; the server checks
      // permissions again on the move itself.
      draggable={isFolder ? canManage : canEditHere}
      onDragStart={(event) => {
        event.stopPropagation();
        event.dataTransfer.effectAllowed = "move";
        // Some browsers refuse to start a drag with no payload attached.
        event.dataTransfer.setData("text/plain", fullPath);
        setDrag({ path: fullPath, isFolder, parentPath });
      }}
      onDragEnd={() => {
        setDrag(null);
        setDropTarget(null);
        cancelSpringOpen();
      }}
      onDragOver={(event) => {
        // Files dragged in from the desktop upload; a catalog entry moves.
        if (isFileDrag(event)) {
          if (!canEditHere) return;
          event.stopPropagation();
          event.preventDefault();
          event.dataTransfer.dropEffect = "copy";
          setFileDropTarget(uploadTarget);
          if (isFolder && !isOpen) scheduleSpringOpen(fullPath);
          return;
        }

        if (!acceptsDrop) return;
        // Stopping propagation makes the innermost folder win, so hovering a
        // nested folder does not also mark its ancestors.
        event.stopPropagation();
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        setDropTarget(fullPath);
        if (!isOpen) scheduleSpringOpen(fullPath);
      }}
      onDragLeave={(event) => {
        // dragleave also fires when entering a child, so ignore those.
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        cancelSpringOpen();
      }}
      onDrop={(event) => {
        if (isFileDrag(event)) {
          if (!canEditHere) return;
          event.stopPropagation();
          event.preventDefault();
          cancelSpringOpen();
          setFileDropTarget(null);
          void actions.uploadFiles(uploadTarget, Array.from(event.dataTransfer.files));
          return;
        }

        if (!acceptsDrop || !drag) return;
        event.stopPropagation();
        event.preventDefault();
        cancelSpringOpen();
        setDropTarget(null);
        const source = drag;
        setDrag(null);
        void actions.moveEntry(source, fullPath);
      }}
      style={indentStyle}
      className={cn(
        "group relative flex h-8 cursor-pointer items-center gap-1.5 pe-1.5 select-none",
        "transition-[background-color,color,opacity] duration-(--duration-instant) ease-(--ease-standard)",
        "focus:outline-none focus-visible:outline-none",
        isSelected
          ? "bg-accent-soft text-accent font-semibold"
          : "text-ink hover:bg-surface-hover",
        isFocused && !isSelected && "bg-surface-hover",
        // The row being dragged fades so the drop target reads clearly.
        isDragging && "opacity-40",
        isDropTarget && "bg-accent-soft outline-2 -outline-offset-2 outline-accent",
        // Upload target uses a dashed outline, so "files land here" never looks
        // like "this entry moves here".
        isUploadTarget &&
          "bg-success-soft outline-2 -outline-offset-2 outline-dashed outline-success",
      )}
    >
      {/* Selection marker, animated in from the left edge. */}
      <span
        aria-hidden="true"
        className={cn(
          "bg-accent absolute inset-y-0 start-0 w-[3px] origin-center rounded-e",
          "transition-transform duration-(--duration-fast) ease-(--ease-standard)",
          isSelected ? "scale-y-100" : "scale-y-0",
        )}
      />

      {isFolder ? (
        <ChevronRight
          size={11}
          aria-hidden="true"
          className={cn(
            "text-ink-subtle shrink-0",
            "transition-transform duration-(--duration-base) ease-(--ease-standard)",
            isOpen && "rotate-90",
          )}
        />
      ) : (
        <span aria-hidden="true" className="w-[11px] shrink-0" />
      )}

      {isFolder ? (
        isOpen ? (
          <Folder2Open size={14} className="text-accent shrink-0" aria-hidden="true" />
        ) : (
          <FolderFill size={14} className="text-ink-subtle shrink-0" aria-hidden="true" />
        )
      ) : (
        // The icon is how you tell a process from a sketch at a glance, so the
        // two kinds get genuinely different glyphs rather than a shared one.
        <FileKindIcon
          kind={diagramKindOf(node.name)}
          className={cn("shrink-0", isSelected ? "text-accent" : "text-ink-subtle")}
        />
      )}

      <span className="min-w-0 flex-1 truncate text-[13px] leading-none">
        <Highlight text={node.name} query={searchQuery} />
      </span>

      {(canEditHere || canManage) && (
        <div
          // Actions stay out of the way until the row is hovered or focused,
          // which is what lets the tree read as a tree instead of a toolbar.
          className={cn(
            "flex shrink-0 items-center gap-0.5",
            "opacity-0 transition-opacity duration-(--duration-fast) ease-(--ease-standard)",
            "group-hover:opacity-100 group-focus-within:opacity-100",
            isFocused && "opacity-100",
          )}
          onClick={(event) => event.stopPropagation()}
        >
          {/* Creating and uploading diagrams is editor work; the subfolder
              button below needs folder authority and is gated separately. */}
          {isFolder && canEditHere && (
            <>
              <Button
                size="xs"
                variant="ghost"
                iconOnly
                title="New BPMN diagram"
                aria-label={`New BPMN diagram in ${node.name}`}
                onClick={() => actions.createDiagram(fullPath, "bpmn")}
              >
                <FileEarmarkPlus size={12} />
              </Button>
              <Button
                size="xs"
                variant="ghost"
                iconOnly
                title="New free-form diagram"
                aria-label={`New free-form diagram in ${node.name}`}
                onClick={() => actions.createDiagram(fullPath, "freeform")}
              >
                <VectorPen size={12} />
              </Button>
              <Button
                size="xs"
                variant="ghost"
                iconOnly
                title="Upload diagram files"
                aria-label={`Upload files to ${node.name}`}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={12} />
              </Button>
            </>
          )}

          {isFolder && canManage && (
            <Button
              size="xs"
              variant="ghost"
              iconOnly
              title="New subfolder"
              aria-label={`New subfolder in ${node.name}`}
              onClick={() => actions.createFolder(fullPath)}
            >
              <FolderPlus size={12} />
            </Button>
          )}

          {/* Renaming or deleting a FOLDER is folder authority; doing either
              to a diagram is not. */}
          {(isFolder ? canManage : canEditHere) && (
            <>
              <Button
                size="xs"
                variant="ghost"
                iconOnly
                title={isFolder ? "Rename folder" : "Rename diagram"}
                aria-label={`Rename ${node.name}`}
                onClick={() =>
                  isFolder ? actions.renameFolder(fullPath) : actions.renameFile(fullPath)
                }
              >
                <PencilSquare size={12} />
              </Button>

              <Button
                size="xs"
                variant="ghost"
                iconOnly
                title={isFolder ? "Delete folder" : "Delete diagram"}
                aria-label={`Delete ${node.name}`}
                className="hover:bg-danger-soft hover:text-danger"
                onClick={() =>
                  isFolder ? actions.deleteFolder(fullPath) : actions.deleteFile(fullPath)
                }
              >
                <Trash size={12} />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );

  if (!isFolder) return row;

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={(event) => {
          const picked = Array.from(event.target.files ?? []);
          event.target.value = ""; // so re-picking the same file fires onChange
          void actions.uploadFiles(fullPath, picked);
        }}
        className="hidden"
        multiple
        accept={UPLOAD_ACCEPT}
      />

      {row}

      {/* grid-template-rows 0fr -> 1fr animates to the subtree's real height,
          so deep folders neither snap open nor clip. */}
      <div className="tree-collapsible" data-open={isOpen}>
        <div className="tree-collapsible-inner">
          {/* `inert` keeps a collapsed subtree out of the tab order and away
              from screen readers while it stays mounted for the animation. */}
          <div role="group" inert={!isOpen} className="group/branch relative">
            {/* Indent guide: a hairline dropping from this folder's chevron so
                nesting depth is traceable without counting indentation. */}
            <span
              aria-hidden="true"
              style={{ insetInlineStart: `${8 + depth * INDENT_PX + 5}px` }}
              className={cn(
                "bg-line absolute inset-y-0 w-px",
                "transition-colors duration-(--duration-fast) ease-(--ease-standard)",
                "group-hover/branch:bg-line-strong",
              )}
            />

            {(node.children ?? []).map((child) => (
              <CatalogNodeItem
                key={joinPath(fullPath, child.name)}
                node={child}
                parentPath={fullPath}
                depth={depth + 1}
                ctx={ctx}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Catalog mutations                                                          */
/* -------------------------------------------------------------------------- */

function useCatalogActions(props: CatalogTreeProps) {
  const dialogs = useDialogs();
  const { apiUrl, token, onCatalogUpdate, onToggleFolder, onSelectFile, onRenamePath, selectedFile } =
    props;

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const fail = useCallback(
    (title: string, error: unknown) => {
      const description =
        error instanceof Error ? error.message : "Please try again.";
      void dialogs.notify({ title, description, tone: "danger" });
    },
    [dialogs],
  );

  /**
   * Turns a failed response into an error carrying what the server actually
   * said. Every call site used to throw a fixed string like "The server
   * rejected the rename", which hid the reason -- a permission denial, a name
   * collision and an unhandled exception were indistinguishable from the UI.
   */
  const serverError = useCallback(async (res: Response, fallback: string) => {
    let detail = "";
    try {
      const text = await res.text();
      if (text.trim()) {
        try {
          detail = JSON.parse(text)?.error ?? "";
        } catch {
          detail = text.slice(0, 200);
        }
      }
    } catch {
      // Body already consumed or unreadable; the status still tells us something.
    }
    return new Error(detail ? `${detail} (HTTP ${res.status})` : `${fallback} (HTTP ${res.status})`);
  }, []);

  return useMemo(() => {
    async function createDiagram(folderPath: string, kind: DiagramKind = "bpmn") {
      const isFreeForm = kind === "freeform";

      const name = await dialogs.prompt({
        title: isFreeForm ? "New free-form diagram" : "New BPMN diagram",
        description: folderPath ? `Created in ${folderPath}` : "Created at the catalog root",
        label: "Diagram name",
        placeholder: isFreeForm ? "Platform architecture" : "Customer onboarding",
        confirmLabel: "Create diagram",
        validate: validateName,
      });
      if (!name) return;

      const fileName = withExtension(name, kind);
      const filePath = folderPath ? `${folderPath}/${fileName}` : fileName;

      // The tree's quick-create always starts blank; the Dashboard's modal is
      // where templates are offered, since that is where there is room to
      // explain them.
      const body = isFreeForm ? freeFormTemplateScene("blank") : MINIMAL_DIAGRAM_XML;
      const contentType = isFreeForm ? "application/json" : "application/xml";

      try {
        const res = await fetch(`${apiUrl}/api/files/${filePath}`, {
          method: "PUT",
          headers: { "Content-Type": contentType, ...authHeaders },
          body,
        });
        if (!res.ok) throw await serverError(res, "Could not create the diagram.");

        onCatalogUpdate();
        if (folderPath) onToggleFolder(folderPath, true);
        onSelectFile(filePath);
      } catch (error) {
        fail("Could not create diagram", error);
      }
    }

    async function createFolder(parentPath: string) {
      const name = await dialogs.prompt({
        title: parentPath ? "New subfolder" : "New root folder",
        description: parentPath ? `Created in ${parentPath}` : undefined,
        label: "Folder name",
        placeholder: "Finance",
        confirmLabel: "Create folder",
        validate: validateName,
      });
      if (!name) return;

      const folderPath = parentPath ? `${parentPath}/${name}` : name;

      try {
        const res = await fetch(`${apiUrl}/api/folders`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({ path: folderPath }),
        });
        if (!res.ok) throw await serverError(res, "Could not create the folder.");

        onCatalogUpdate();
        onToggleFolder(folderPath, true);
      } catch (error) {
        fail("Could not create folder", error);
      }
    }

    async function renameFile(path: string) {
      const parent = path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
      const currentName = path.split("/").pop() ?? path;

      const input = await dialogs.prompt({
        title: "Rename diagram",
        label: "Diagram name",
        initialValue: currentName,
        confirmLabel: "Rename",
        validate: validateName,
      });
      if (!input) return;

      // Preserve the original extension when the user omits one.
      const hasExtension = /\.[^./\\]+$/.test(input);
      const extension = currentName.includes(".")
        ? currentName.slice(currentName.lastIndexOf("."))
        : "";
      const newName = !hasExtension && extension ? `${input}${extension}` : input;

      const to = parent ? `${parent}/${newName}` : newName;
      if (to === path) return;

      try {
        const res = await fetch(`${apiUrl}/api/rename/file`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({ from: path, to }),
        });
        if (!res.ok) throw await serverError(res, "Could not rename.");

        if (selectedFile === path) onSelectFile(to);
        onCatalogUpdate();
      } catch (error) {
        fail("Could not rename diagram", error);
      }
    }

    async function renameFolder(path: string) {
      const parent = path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
      const currentName = path.split("/").pop() ?? path;

      const newName = await dialogs.prompt({
        title: "Rename folder",
        label: "Folder name",
        initialValue: currentName,
        confirmLabel: "Rename",
        validate: validateName,
      });
      if (!newName) return;

      const to = parent ? `${parent}/${newName}` : newName;
      if (to === path) return;

      try {
        const res = await fetch(`${apiUrl}/api/rename/folder`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({ from: path, to }),
        });
        if (!res.ok) throw await serverError(res, "Could not rename.");

        onRenamePath?.(path, to, true);
        onCatalogUpdate();
      } catch (error) {
        fail("Could not rename folder", error);
      }
    }

    async function deleteFile(path: string) {
      const confirmed = await dialogs.confirm({
        title: "Delete this diagram?",
        description: `"${path}" will be permanently removed. This cannot be undone.`,
        confirmLabel: "Delete diagram",
        tone: "danger",
      });
      if (!confirmed) return;

      try {
        const res = await fetch(`${apiUrl}/api/files/${path}`, {
          method: "DELETE",
          headers: authHeaders,
        });
        if (!res.ok) throw await serverError(res, "Could not delete.");
        onCatalogUpdate();
      } catch (error) {
        fail("Could not delete diagram", error);
      }
    }

    async function deleteFolder(path: string) {
      const confirmed = await dialogs.confirm({
        title: "Delete this folder?",
        description: `"${path}" and everything inside it will be permanently removed. This cannot be undone.`,
        confirmLabel: "Delete folder",
        tone: "danger",
      });
      if (!confirmed) return;

      try {
        const res = await fetch(`${apiUrl}/api/folders`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({ path }),
        });
        if (!res.ok) throw await serverError(res, "Could not delete.");
        onCatalogUpdate();
      } catch (error) {
        fail("Could not delete folder", error);
      }
    }

    /**
     * Uploads .bpmn files into `folderPath` ("" = catalog root).
     *
     * Takes a plain File list rather than an input event, so the file picker and
     * a drag from the desktop both land here.
     */
    async function uploadFiles(folderPath: string, fileList: File[]) {
      if (fileList.length === 0) return;

      const isDiagram = (file: File) => diagramKindOf(file.name) !== null;
      const accepted = fileList.filter(isDiagram);
      const rejected = fileList.filter((file) => !isDiagram(file));

      if (rejected.length > 0) {
        await dialogs.notify({
          title:
            rejected.length === 1
              ? "One file was skipped"
              : `${rejected.length} files were skipped`,
          description: `Only ${DIAGRAM_EXTENSIONS.join(" and ")} files can be uploaded. Skipped: ${rejected
            .map((file) => file.name)
            .join(", ")}`,
          tone: "danger",
        });
      }

      if (accepted.length === 0) return;

      const targetLabel = folderPath || "the catalog root";

      try {
        const results = await Promise.all(
          accepted.map((file) => {
            const target = folderPath ? `${folderPath}/${file.name}` : file.name;
            return fetch(`${apiUrl}/api/files/${target}`, {
              method: "PUT",
              headers: { "Content-Type": "application/xml", ...authHeaders },
              body: file,
            });
          }),
        );

        const locked = results.filter((res) => res.status === 423).length;
        const failed = results.filter((res) => !res.ok).length;

        if (locked > 0) {
          throw new Error(
            `${locked} file(s) are currently being edited by someone else and were not replaced.`,
          );
        }
        if (failed > 0) {
          throw new Error(
            `${failed} of ${accepted.length} file(s) were rejected by the server.`,
          );
        }

        onCatalogUpdate();
        if (folderPath) onToggleFolder(folderPath, true);
      } catch (error) {
        fail(`Could not upload to ${targetLabel}`, error);
        onCatalogUpdate();
      }
    }

    /**
     * Moves a dragged entry into `targetFolder` ("" = catalog root).
     *
     * Both /api/rename endpoints are real moves -- they copy to the new path,
     * delete the old one, and re-check permissions on the source and the target
     * parent -- so a move needs no extra server support.
     */
    async function moveEntry(source: DragSource, targetFolder: string) {
      if (!canDropInto(source, targetFolder)) return;

      const name = source.path.split("/").pop() ?? source.path;
      const to = targetFolder ? `${targetFolder}/${name}` : name;
      if (to === source.path) return;

      const endpoint = source.isFolder ? "/api/rename/folder" : "/api/rename/file";

      try {
        const res = await fetch(`${apiUrl}${endpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({ from: source.path, to }),
        });

        if (res.status === 409) {
          await dialogs.notify({
            title: "Something with that name is already there",
            description: `"${targetFolder || "The catalog root"}" already contains "${name}". Rename one of them first.`,
            tone: "danger",
          });
          return;
        }
        if (res.status === 403) {
          await dialogs.notify({
            title: "You cannot move that there",
            description: "You need edit access to both the item and the destination folder.",
            tone: "danger",
          });
          return;
        }
        if (!res.ok) throw await serverError(res, "Could not move.");

        // Keeps expanded folders and the open diagram pointing at the new path.
        onRenamePath?.(source.path, to, source.isFolder);
        if (!source.isFolder && selectedFile === source.path) onSelectFile(to);
        if (targetFolder) onToggleFolder(targetFolder, true);
        onCatalogUpdate();
      } catch (error) {
        fail(`Could not move "${name}"`, error);
        onCatalogUpdate();
      }
    }

    return {
      createDiagram,
      createFolder,
      renameFile,
      renameFolder,
      deleteFile,
      deleteFolder,
      uploadFiles,
      moveEntry,
    };
  }, [
    apiUrl,
    authHeaders,
    dialogs,
    fail,
    onCatalogUpdate,
    onSelectFile,
    onToggleFolder,
    onRenamePath,
    selectedFile,
  ]);
}

/* -------------------------------------------------------------------------- */
/* Tree                                                                       */
/* -------------------------------------------------------------------------- */

export default function CatalogTree(props: CatalogTreeProps) {
  const { catalog, isAdmin, openPaths, onToggleFolder, onSelectFile, searchQuery } = props;

  const actions = useCatalogActions(props);
  const [focusedPath, setFocusedPath] = useState<string | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());

  const [drag, setDrag] = useState<DragSource | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [fileDropTarget, setFileDropTarget] = useState<string | null>(null);

  const registerRow = useCallback((path: string, el: HTMLDivElement | null) => {
    if (el) rowRefs.current.set(path, el);
    else rowRefs.current.delete(path);
  }, []);

  /* --- Spring-loaded folders --------------------------------------------- */
  // Hovering a closed folder mid-drag opens it, so a deep destination can be
  // reached without dropping and re-picking the item up.

  const springRef = useRef<{ path: string; timer: number } | null>(null);

  const cancelSpringOpen = useCallback(() => {
    if (springRef.current) {
      window.clearTimeout(springRef.current.timer);
      springRef.current = null;
    }
  }, []);

  const scheduleSpringOpen = useCallback(
    (path: string) => {
      if (springRef.current?.path === path) return;
      cancelSpringOpen();
      springRef.current = {
        path,
        timer: window.setTimeout(() => {
          springRef.current = null;
          onToggleFolder(path, true);
        }, 550),
      };
    },
    [cancelSpringOpen, onToggleFolder],
  );

  // A drag can end outside the tree (Escape, or a drop on another app), which
  // fires no dragend on our rows -- so clear the pending timer on unmount too.
  useEffect(() => cancelSpringOpen, [cancelSpringOpen]);

  const visibleRows = useMemo(
    () => flattenVisible(catalog, openPaths),
    [catalog, openPaths],
  );

  // Keep the roving tabindex anchored to a row that actually exists, otherwise
  // the tree loses its single tab stop after a delete, rename or filter change.
  useEffect(() => {
    if (visibleRows.length === 0) {
      if (focusedPath !== null) setFocusedPath(null);
      return;
    }
    if (!focusedPath || !visibleRows.some((row) => row.path === focusedPath)) {
      setFocusedPath(visibleRows[0].path);
    }
  }, [visibleRows, focusedPath]);

  const moveFocus = useCallback((path: string) => {
    setFocusedPath(path);
    rowRefs.current.get(path)?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!focusedPath) return;

      const index = visibleRows.findIndex((row) => row.path === focusedPath);
      if (index === -1) return;
      const current = visibleRows[index];

      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          const next = visibleRows[index + 1];
          if (next) moveFocus(next.path);
          break;
        }

        case "ArrowUp": {
          event.preventDefault();
          const previous = visibleRows[index - 1];
          if (previous) moveFocus(previous.path);
          break;
        }

        case "ArrowRight": {
          event.preventDefault();
          if (!current.isFolder) break;
          if (!current.isOpen) {
            onToggleFolder(current.path, true);
          } else {
            const child = visibleRows[index + 1];
            if (child && child.parentPath === current.path) moveFocus(child.path);
          }
          break;
        }

        case "ArrowLeft": {
          event.preventDefault();
          if (current.isFolder && current.isOpen) {
            onToggleFolder(current.path, false);
          } else if (current.parentPath) {
            moveFocus(current.parentPath);
          }
          break;
        }

        case "Home": {
          event.preventDefault();
          moveFocus(visibleRows[0].path);
          break;
        }

        case "End": {
          event.preventDefault();
          moveFocus(visibleRows[visibleRows.length - 1].path);
          break;
        }

        case "Enter":
        case " ": {
          event.preventDefault();
          if (current.isFolder) onToggleFolder(current.path, !current.isOpen);
          else onSelectFile(current.path);
          break;
        }

        default:
          break;
      }
    },
    [focusedPath, visibleRows, moveFocus, onToggleFolder, onSelectFile],
  );

  const ctx: TreeContext = {
    props,
    actions,
    focusedPath,
    setFocusedPath,
    registerRow,
    drag,
    setDrag,
    dropTarget,
    setDropTarget,
    scheduleSpringOpen,
    cancelSpringOpen,
    fileDropTarget,
    setFileDropTarget,
  };

  const isEmpty = catalog.length === 0;

  // Dropping on the tree's own background moves the entry to the catalog root.
  // Row handlers stopPropagation, so this only fires on the gaps between rows.
  const rootAcceptsDrop = Boolean(isAdmin) && canDropInto(drag, "");
  const isRootDropTarget = rootAcceptsDrop && dropTarget === "";

  // Only admins may write at the catalog root, matching the root create buttons.
  const rootAcceptsUpload = Boolean(isAdmin);
  const isRootUploadTarget = rootAcceptsUpload && fileDropTarget === "";

  const rootDropHandlers = {
    onDragOver: (event: React.DragEvent) => {
      if (isFileDrag(event)) {
        if (!rootAcceptsUpload) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setFileDropTarget("");
        cancelSpringOpen();
        return;
      }

      if (!rootAcceptsDrop) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setDropTarget("");
      cancelSpringOpen();
    },

    onDrop: (event: React.DragEvent) => {
      if (isFileDrag(event)) {
        if (!rootAcceptsUpload) return;
        event.preventDefault();
        setFileDropTarget(null);
        void actions.uploadFiles("", Array.from(event.dataTransfer.files));
        return;
      }

      if (!rootAcceptsDrop || !drag) return;
      event.preventDefault();
      const source = drag;
      setDrag(null);
      setDropTarget(null);
      void actions.moveEntry(source, "");
    },

    // A file drag that leaves the tree entirely, or is cancelled with Escape,
    // fires no drop -- so clear the highlight here as well.
    onDragLeave: (event: React.DragEvent) => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
      setFileDropTarget(null);
      setDropTarget(null);
      cancelSpringOpen();
    },
  };

  return (
    // Drop handling lives on the outer wrapper so it also covers the empty-catalog
    // state, where no tree element exists. Row handlers stopPropagation, so this
    // only sees drops that missed every row.
    <div
      {...rootDropHandlers}
      className={cn(
        "flex min-h-0 flex-col",
        isRootUploadTarget && "bg-success-soft/40",
      )}
    >
      {isAdmin && (
        <div className="mb-2 flex gap-1.5 px-2">
          <Button
            size="sm"
            variant="secondary"
            className="flex-1"
            onClick={() => actions.createFolder("")}
          >
            <FolderPlus size={13} />
            New folder
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="flex-1"
            title="New BPMN diagram at the catalog root"
            onClick={() => actions.createDiagram("", "bpmn")}
          >
            <Diagram2 size={13} />
            BPMN
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="flex-1"
            title="New free-form diagram at the catalog root"
            onClick={() => actions.createDiagram("", "freeform")}
          >
            <VectorPen size={13} />
            Free-form
          </Button>
        </div>
      )}

      {isEmpty ? (
        <div className="animate-fade-in px-4 py-10 text-center">
          <FolderFill size={22} className="text-ink-subtle/50 mx-auto mb-2.5" />
          <p className="text-ink-muted text-[13px] font-medium">
            {searchQuery ? "No matching diagrams" : "This catalog is empty"}
          </p>
          <p className="text-ink-subtle mt-1 text-xs">
            {searchQuery
              ? "Try a different search term."
              : isAdmin
                ? "Create a folder, or drop diagram files here."
                : "Ask an administrator for access."}
          </p>
        </div>
      ) : (
        <div
          role="tree"
          aria-label="Diagram catalog"
          onKeyDown={handleKeyDown}
          className={cn(
            "animate-fade-in min-h-full",
            isRootDropTarget && "bg-accent-soft/40 outline-2 -outline-offset-2 outline-accent",
            isRootUploadTarget &&
              "bg-success-soft/50 outline-2 -outline-offset-2 outline-dashed outline-success",
          )}
        >
          {catalog.map((node) => (
            <CatalogNodeItem key={node.name} node={node} parentPath="" depth={0} ctx={ctx} />
          ))}

          {drag && (
            <p className="text-ink-subtle animate-fade-in px-3 py-2 text-center text-[11px]">
              Drop on a folder to move it there, or here for the catalog root
            </p>
          )}
        </div>
      )}

      {/* Naming the destination matters most for uploads, because dropping on a
          diagram targets the folder around it rather than the row itself. */}
      {fileDropTarget !== null && (
        <p className="text-success animate-fade-in px-3 py-2 text-center text-[11px] font-medium">
          Release to upload diagram files to{" "}
          <span className="font-semibold">{fileDropTarget || "the catalog root"}</span>
        </p>
      )}
    </div>
  );
}
